import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Upload, Link2, Loader2, Plus } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { ACard, AH1, ABtn, AField } from "./ui";

const ReelForm = ({ reel, onDone }) => {
  const [url, setUrl] = useState(reel?.source === "url" ? reel.src : "");
  const [title, setTitle] = useState(reel?.title || "");
  const [pct, setPct] = useState(null);
  const send = async (file) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("url", file ? "" : url);
    fd.append("title", title);
    setPct(0);
    try {
      const cfg = { onUploadProgress: (e) => setPct(Math.round((100 * e.loaded) / (e.total || 1))) };
      if (reel) await api.put(`/admin/reels/${reel.id}`, fd, cfg); else await api.post("/admin/reels", fd, cfg);
      toast.success("تم الحفظ");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setPct(null); }
  };
  const tid = reel ? reel.id : "new";
  return (
    <div className="space-y-3">
      <AField label="عنوان (اختياري)" value={title} onChange={(e) => setTitle(e.target.value)} testid={`reel-title-${tid}`} />
      <label className="flex items-center gap-2 cursor-pointer rounded-xl border border-dashed border-white/15 hover:border-[var(--kh-violet-2)] p-3 text-sm text-slate-300">
        {pct === null ? <Upload size={16} /> : <Loader2 size={16} className="animate-spin" />}
        {pct === null ? (reel ? "استبدال بملف فيديو" : "رفع ملف فيديو (MP4 رأسي 9:16)") : `جاري الرفع ${pct}%`}
        <input type="file" hidden accept="video/mp4,video/webm,video/quicktime" disabled={pct !== null}
          onChange={(e) => e.target.files[0] && send(e.target.files[0])} data-testid={`reel-file-${tid}`} />
      </label>
      <div className="flex gap-2">
        <AField label="أو رابط فيديو مباشر (MP4)" dir="ltr" placeholder="https://.../video.mp4" value={url} onChange={(e) => setUrl(e.target.value)} testid={`reel-url-${tid}`} className="flex-1" />
        <ABtn variant="ghost" className="self-end" disabled={pct !== null || !url} onClick={() => send(null)} data-testid={`reel-save-url-${tid}`}><Link2 size={15} />حفظ الرابط</ABtn>
      </div>
    </div>
  );
};

export default function AdminReels() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/reels").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const move = async (i, d) => {
    const ids = list.map((r) => r.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    await api.post("/admin/reels/reorder", { ids });
    load();
  };
  const del = async (r) => {
    if (!window.confirm("حذف الفيديو؟")) return;
    await api.delete(`/admin/reels/${r.id}`);
    toast.success("تم الحذف");
    load();
  };
  return (
    <div data-testid="admin-reels">
      <AH1>أعمال المتدربين</AH1>
      <p className="text-sm text-slate-400 mb-6">الفيديوهات دي بتظهر في الصفحة الرئيسية (حتى 3 فيديوهات رأسية 9:16)، بتشتغل تلقائي من غير صوت وبتتكرر.</p>
      <div className="grid lg:grid-cols-3 gap-5">
        {list.map((r, i) => (
          <ACard key={r.id} className="space-y-4" data-testid={`admin-reel-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">فيديو {i + 1}</span>
              <div className="flex gap-1">
                <ABtn variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`reel-up-${i}`}><ArrowUp size={14} /></ABtn>
                <ABtn variant="ghost" disabled={i === list.length - 1} onClick={() => move(i, 1)} data-testid={`reel-down-${i}`}><ArrowDown size={14} /></ABtn>
                <ABtn variant="danger" onClick={() => del(r)} data-testid={`reel-delete-${i}`}><Trash2 size={14} /></ABtn>
              </div>
            </div>
            <video src={mediaUrl(r.src)} muted loop autoPlay playsInline className="w-40 mx-auto aspect-[9/16] object-cover rounded-xl bg-black" />
            <ReelForm reel={r} onDone={load} />
          </ACard>
        ))}
        {list.length < 3 && (
          <ACard className="space-y-4 border-dashed" data-testid="admin-reel-new">
            <span className="flex items-center gap-2 text-sm font-semibold"><Plus size={16} />إضافة فيديو</span>
            <ReelForm onDone={load} />
          </ACard>
        )}
      </div>
    </div>
  );
}
